import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ChartLine, CircleNotch, Warning } from '@phosphor-icons/react'
import { apiService } from '@/services/api'
import type { User } from '@/types'

interface EngagementPanelProps {
  user?: User
}

interface EngagementUser {
  id: string
  full_name: string
  email: string
  role: string
  logins_in_period: number
  last_login: string | null
}

const PERIODS = [7, 30, 90]
const INACTIVE_DAYS = 14

function daysSince(iso: string | null): number | null {
  if (!iso) return null
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24))
}

function formatLastLogin(iso: string | null): string {
  if (!iso) return 'Nunca acessou'
  const days = daysSince(iso) ?? 0
  if (days === 0) return 'Hoje'
  if (days === 1) return 'Ontem'
  return `Há ${days} dias`
}

export function EngagementPanel({ user }: EngagementPanelProps) {
  const [days, setDays] = useState(30)
  const [users, setUsers] = useState<EngagementUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    apiService
      .adminGetEngagement(days)
      .then((data) => setUsers(data.users))
      .catch(() => setError('Não foi possível carregar os dados de engajamento.'))
      .finally(() => setLoading(false))
  }, [days])

  if (user && user.role !== 'professor' && user.role !== 'superadmin') {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Warning size={20} />
        Acesso restrito a professores.
      </div>
    )
  }

  const students = users.filter((u) => u.role === 'estudante')
  const inactive = students.filter((u) => {
    const d = daysSince(u.last_login)
    return d === null || d > INACTIVE_DAYS
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ChartLine size={28} className="text-primary" />
            Engajamento
          </h1>
          <p className="text-muted-foreground">
            Acessos à plataforma por usuário no período
          </p>
        </div>
        <div className="flex gap-2">
          {PERIODS.map((p) => (
            <Button
              key={p}
              variant={days === p ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(p)}
            >
              {p} dias
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:max-w-md">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground font-medium uppercase">Estudantes</p>
            <p className="text-2xl font-bold">{students.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-xs text-muted-foreground font-medium uppercase">
              Sem acesso há {INACTIVE_DAYS}+ dias
            </p>
            <p className={`text-2xl font-bold ${inactive.length > 0 ? 'text-destructive' : ''}`}>
              {inactive.length}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Acessos por usuário</CardTitle>
          <CardDescription>
            Logins nos últimos {days} dias, ordenados do acesso mais recente ao mais antigo
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
              <CircleNotch size={20} className="animate-spin" />
              Carregando...
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-destructive py-8 justify-center">
              <Warning size={20} />
              {error}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Usuário</th>
                    <th className="py-2 pr-4 font-medium">Papel</th>
                    <th className="py-2 pr-4 font-medium text-right">Acessos</th>
                    <th className="py-2 font-medium">Último acesso</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const d = daysSince(u.last_login)
                    const isInactive = d === null || d > INACTIVE_DAYS
                    return (
                      <tr key={u.id} className="border-b border-border/50">
                        <td className="py-2 pr-4">
                          <div className="font-medium">{u.full_name}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </td>
                        <td className="py-2 pr-4">
                          <Badge variant={u.role === 'estudante' ? 'secondary' : 'default'}>
                            {u.role === 'estudante' ? 'Estudante' : u.role === 'professor' ? 'Professor' : 'Admin'}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4 text-right font-mono">{u.logins_in_period}</td>
                        <td className={`py-2 ${isInactive ? 'text-destructive font-medium' : ''}`}>
                          {formatLastLogin(u.last_login)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
